import React, { useState, useEffect, useCallback } from 'react';
import { theme } from '../theme';

interface NotificationAction {
  label: string;
  onClick: () => void;
}

interface NotificationProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
  onClose: () => void;
  action?: NotificationAction;
  durationMs?: number;
}

export const Notification: React.FC<NotificationProps> = ({ message, type, isVisible, onClose, action, durationMs = 4000 }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onClose, durationMs);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose, durationMs]);

  if (!isVisible) return null;

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    top: theme.spacing.lg,
    right: theme.spacing.lg,
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    borderRadius: theme.borderRadius.card,
    color: theme.colors.text,
    fontWeight: theme.fonts.weightBold,
    fontSize: theme.fonts.sizeBody,
    zIndex: 1000,
    maxWidth: 460,
    boxShadow: theme.colors.shadow,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.surface,
    border: `2px solid ${theme.colors.primary}`,
    fontFamily: theme.fonts.body,
  };

  const typeStyles: Record<string, React.CSSProperties> = {
    success: { backgroundColor: theme.colors.success, color: theme.colors.white, border: `2px solid ${theme.colors.success}` },
    error: { backgroundColor: theme.colors.danger, color: theme.colors.white, border: `2px solid ${theme.colors.danger}` },
    warning: { backgroundColor: theme.colors.warning, color: theme.colors.text, border: `2px solid ${theme.colors.warning}` },
    info: { backgroundColor: theme.colors.primary, color: theme.colors.white, border: `2px solid ${theme.colors.primary}` },
  };

  const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️';

  return (
    <div style={{ ...baseStyle, ...typeStyles[type] }} data-testid="notification">
      <span>{icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      {action && (
        <button
          type="button"
          onClick={() => { action.onClick(); onClose(); }}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'inherit',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: theme.borderRadius.button,
            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
            fontWeight: theme.fonts.weightBold,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.9em',
            whiteSpace: 'nowrap',
          }}
          data-testid="notification-action"
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer', opacity: 0.8 }}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
};

type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationState {
  message: string;
  type: NotificationType;
  isVisible: boolean;
  action?: NotificationAction;
  durationMs?: number;
}

interface ShowOptions {
  action?: NotificationAction;
  durationMs?: number;
}

export const useNotification = () => {
  const [notification, setNotification] = useState<NotificationState>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  const showNotification = useCallback((message: string, type: NotificationType = 'info', options?: ShowOptions) => {
    setNotification({ message, type, isVisible: true, action: options?.action, durationMs: options?.durationMs });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  }, []);

  return { notification, showNotification, hideNotification };
};
